import { store } from "src/domain/app/redux/store";
import {
  addMessage,
  addNotification,
  incrementUnreadCount,
  removeMessage,
  setRooms,
} from "src/domain/app/redux/chat/chat-slice";
import { chatService } from "src/shared/services/chat";
import { signalRHub } from "src/shared/services/chat/signalr-hub";
import { getMessagePreview } from "src/shared/services/chat/attachments";
import { ChatMessageDto } from "src/shared/services/chat/dto";

// Listeners must be registered exactly once for the whole app: a second
// registration would count every incoming message twice.
let listenersInitialized = false;
let roomsRequestInFlight: Promise<void> | null = null;

/**
 * Loads the chat rooms into the store, so the unread badge is correct before
 * the chat popup is ever opened. Concurrent calls share one request.
 */
export const loadChatRooms = (): Promise<void> => {
  if (roomsRequestInFlight) {
    return roomsRequestInFlight;
  }

  roomsRequestInFlight = chatService
    .getUserChatRooms()
    .then((response) => {
      if (response?.Data) {
        store.dispatch(setRooms(response.Data.data));
      }
    })
    .catch((error) => {
      console.error("Failed to load chat rooms:", error);
    })
    .finally(() => {
      roomsRequestInFlight = null;
    });

  return roomsRequestInFlight;
};

const showDesktopNotification = (message: ChatMessageDto) => {
  if ("Notification" in window && Notification.permission === "granted") {
    new Notification("New Message", {
      body: `${message.senderName}: ${getMessagePreview(message)}`,
      icon: "/logo192.png",
    });
  }
};

export const setupGlobalChatListeners = () => {
  if (listenersInitialized) {
    return;
  }
  listenersInitialized = true;

  signalRHub.onReceiveMessage((message: ChatMessageDto) => {
    const state = store.getState();
    const currentRoomId = state.chat?.currentRoomId;
    const muted = state.chat?.mutedRooms ?? [];
    const profile = state.auth?.myProfile;
    const isOwnMessage = !!profile && message.senderId === profile.id;

    // The server pushes to both the room group and the user group, so the same
    // message can arrive twice and must only be counted once.
    const alreadyReceived = (state.chat?.messages?.[message.chatRoomId] ?? [])
      .some((m) => m.id === message.id);

    store.dispatch(addMessage({ roomId: message.chatRoomId, message }));

    if (alreadyReceived || isOwnMessage || currentRoomId === message.chatRoomId) {
      return;
    }

    const isKnownRoom = (state.chat?.rooms ?? []).some(
      (room) => room.id === message.chatRoomId,
    );
    if (isKnownRoom) {
      store.dispatch(incrementUnreadCount(message.chatRoomId));
    } else {
      // A room created after login isn't in the store yet, so there is no
      // badge to bump — pull the list in with its server-side unread counts.
      loadChatRooms();
    }

    if (!muted.includes(message.chatRoomId)) {
      store.dispatch(addNotification({ roomId: message.chatRoomId, message }));
      showDesktopNotification(message);
    }
  });

  signalRHub.onMessageDeleted((messageId: string) => {
    const currentRoom = store.getState().chat?.currentRoomId;
    if (currentRoom) {
      store.dispatch(removeMessage({ roomId: currentRoom, messageId }));
    }
  });

  // Messages sent while the connection was down are never pushed, so re-sync
  // the rooms (and their unread counts) whenever the connection comes back.
  signalRHub.onReconnected(() => {
    loadChatRooms();
  });
};
